import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Wand2, Clock, CalendarCheck, Plane, Activity, Home } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { TUMOR_GUIDE, TREATMENTS } from "../content/siteContent";
import { locT, locTumor } from "../content/i18n_extra";
import { locDetail } from "../content/details_i18n";
import { track } from "../lib/api";
import { SpeakButton } from "./SpeakButton";

const STAGE_LOCAL = ["surgery", "heavyion", "proton", "bnct", "photoimmuno", "aftv", "igtc"];
const STAGE_SYSTEMIC = ["cart", "ipsc", "immuno", "aftv", "igtc", "regen", "ainpros", "bnct", "photoimmuno"];

export function BudgetWizard() {
  const { t, lang } = useLang();
  const [selected, setSelected] = useState(null);
  const [stage, setStage] = useState(null);

  const active = TUMOR_GUIDE.find((g) => g.id === selected);
  let recommended = active ? active.treatments.map((id) => TREATMENTS.find((x) => x.id === id)).filter(Boolean) : [];
  if (stage) {
    const pref = stage === "early" ? STAGE_LOCAL : STAGE_SYSTEMIC;
    const filtered = recommended.filter((r) => pref.includes(r.id));
    if (filtered.length) recommended = filtered;
  }
  const durations = recommended.map((r) => locDetail(r.id, lang).duration).filter(Boolean);

  const stages = [
    { id: "early", label: t("wiz_stage_early") },
    { id: "advanced", label: t("wiz_stage_advanced") },
    { id: "recurrent", label: t("wiz_stage_recurrent") },
  ];

  const timeline = [
    { icon: Activity, label: t("wiz_eval"), val: t("wiz_eval_d") },
    { icon: Plane, label: t("wiz_visa"), val: t("wiz_visa_d") },
    { icon: Clock, label: t("wiz_treat"), val: durations[0] || "—" },
    { icon: Home, label: t("wiz_follow"), val: t("wiz_follow_d") },
  ];

  const scrollToForm = () => { track("wizard_cta"); document.getElementById("ikinci-gorus")?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <section id="sihirbaz" className="bg-white py-28 sm:py-36 border-t border-black/5" data-testid="budget-wizard-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <div className="flex items-center gap-2.5 text-xs font-heading font-bold tracking-[0.18em] sm:tracking-[0.3em] uppercase text-brand mb-4">
            <Wand2 size={15} /> {t("wiz_kicker")}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("wiz_title")}</h2>
          <p className="mt-5 text-lg text-sub leading-relaxed">{t("wiz_sub")}</p>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-2.5" data-testid="wizard-chips">
          {TUMOR_GUIDE.map((g) => (
            <button
              key={g.id}
              data-testid={`wizard-chip-${g.id}`}
              onClick={() => setSelected(selected === g.id ? null : g.id)}
              className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border ${selected === g.id ? "bg-brand text-white border-brand shadow-lg shadow-brand/25" : "bg-ice text-ink/70 border-black/10 hover:border-brand/40 hover:text-brand"}`}
            >
              {locTumor(g, lang)}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active.id}
              data-testid="wizard-result"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45 }}
              className="mt-10"
            >
              <div className="flex flex-wrap items-center gap-2.5 mb-6" data-testid="wizard-stages">
                <span className="text-xs font-heading font-bold tracking-[0.25em] uppercase text-sub mr-2">{t("wiz_step2")}:</span>
                {stages.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`wizard-stage-${s.id}`}
                    onClick={() => setStage(stage === s.id ? null : s.id)}
                    className={`px-4 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 border ${stage === s.id ? "bg-ink text-white border-ink" : "bg-white text-ink/60 border-black/10 hover:border-ink/40"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-ice rounded-3xl border border-brand/10 p-8">
                <div className="text-xs font-heading font-bold tracking-[0.25em] uppercase text-brand">{t("wiz_reco")}</div>
                <div className="mt-5 space-y-3">
                  {recommended.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 bg-white rounded-2xl border border-black/5 px-4 py-3" data-testid={`wizard-reco-${r.id}`}>
                      <span className="w-2 h-2 rounded-full bg-brand shrink-0" />
                      <span className="font-semibold text-ink text-sm">{locT(r, lang).title}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-deepsea text-white rounded-3xl p-8">
                <div className="flex items-center gap-2.5 text-xs font-heading font-bold tracking-[0.25em] uppercase text-white/60">
                  <CalendarCheck size={15} /> {t("wiz_title")}
                </div>
                <div className="mt-6 space-y-1">
                  {timeline.map((s, i) => (
                    <div key={i} className="flex items-center gap-4 py-3.5 border-b border-white/10 last:border-0" data-testid={`wizard-step-${i}`}>
                      <span className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0"><s.icon size={17} /></span>
                      <span className="flex-1 text-sm text-white/80">{s.label}</span>
                      <span className="text-sm font-heading font-bold text-white">{s.val}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={scrollToForm}
                    data-testid="wizard-cta-button"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand hover:bg-brand/90 text-white font-semibold text-sm px-6 py-3.5 transition-colors"
                  >
                    {t("wiz_cta")} <ArrowRight size={16} />
                  </button>
                  <SpeakButton
                    light
                    testid="wizard-speak"
                    label={t("tudi_listen_page")}
                    getText={() => {
                      const names = recommended.map((r) => locT(r, lang).title).join(", ");
                      const tl = timeline.map((s) => `${s.label}: ${s.val}`).join(". ");
                      return `${t("wiz_reco")}: ${names}. ${tl}.`;
                    }}
                  />
                </div>
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
